import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { withTranslation } from 'react-i18next';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { createFulltextItem } from 'shared/lib/searchHelpers';
import { propTypes as searchItemPropTypes } from 'shared/proptypes/SearchItem';
import Autocomplete from 'shared/components/Autocomplete';
import Tag from 'shared/components/Tag';
import Icon from 'shared/components/Icon';
import { DEFAULT_CURRENCY, TRANSLATED_TYPES, getTranslatedTypePath } from 'shared/constants';
import { createMoneyFormatter } from 'shared/lib/formatters';
import apiClient from 'userportal/lib/ApiClient';
import styles from './AnySuggestion.module.scss';

const typeOrder = {
  field: 1,
  role: 2,
  skill: 3,
  country: 4,
  city: 5,
  company: 6,
  employment: 7
};

const rateRegexp = /^[0-9]+[0-9 .,]+$/;
const changeLevelAllowed = ['language', 'skill'];

export class AnySuggestion extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    selected: PropTypes.arrayOf(PropTypes.shape(searchItemPropTypes)),
    loading: PropTypes.bool,
    onChange: PropTypes.func,
    onSelect: PropTypes.func,
    onRemoveLastSelected: PropTypes.func,
    onToggleRequired: PropTypes.func,
    onToggleMode: PropTypes.func,
    onChangeLevel: PropTypes.func,
    onRemove: PropTypes.func,
    disabled: PropTypes.bool,
    currency: PropTypes.string,
    externalValue: PropTypes.string,
    isInverseSearch: PropTypes.bool,
  };

  static defaultProps = {
    id: 'anysuggestion',
    selected: [],
    loading: false,
    onChange() {},
    onSelect() {},
    onRemoveLastSelected() {},
    onToggleRequired() {},
    onToggleMode() {},
    onChangeLevel: null,
    onRemove() {},
    disabled: false,
    currency: DEFAULT_CURRENCY,
    externalValue: null,
    isInverseSearch: false,
  };

  constructor(props) {
    super(props);
    this.formatMoney = createMoneyFormatter(this.props.i18n.language);
  }

  createUnknown = (value) => {
    const { t, currency, isInverseSearch } = this.props;
    const fulltextItem = createFulltextItem(t, value, isInverseSearch);

    if (rateRegexp.test(value)) {
      const rate = value.replace(/[^0-9]+/, '').trim();
      const rateItem = {
        title: t(
          isInverseSearch ? 'suggestion.typeRateInverse' : 'suggestion.typeRate',
          { value: this.formatMoney(rate, currency) }
        ),
        slug: rate,
        type: 'rate'
      };

      return [rateItem, fulltextItem];
    }

    return [fulltextItem];
  };

  getSuggestionTitle = (suggestion) => {
    const { t } = this.props;
    if (TRANSLATED_TYPES.includes(suggestion.type) && suggestion.slug) {
      return t(getTranslatedTypePath(suggestion.type, suggestion.slug));
    }

    if (suggestion.type === 'language') {
      return t(`language.${suggestion.slug}`);
    }

    if (suggestion.type === 'homeoffice') {
      if (~~suggestion.slug === 0) {
        return t('suggestion.typeHomeofficeNa');
      }

      return `${t('suggestion.typeHomeoffice')} ${t('common:suggestion.typeHomeofficeValue', { postProcess: 'interval', count: ~~suggestion.slug })}`;
    }

    return suggestion.title;
  };

  loadSuggestions = async (q) => {
    if (!q) {
      return [];
    }

    try {
      const response = await apiClient.post('/suggest/any', {
        q,
        roles: [],
        exclude: []
      });
      const hits = _.sortBy(response.hits.map(hit => ({
        ...hit,
        title: this.getSuggestionTitle(hit),
        o: typeOrder[hit.type] || 99
      })), 'o');
      if (hits.length > 0) {
        return [...hits, ...this.createUnknown(q)];
      }

      return hits;
    } catch (err) {
      Sentry.captureException(err);
      return [];
    }
  };

  onSelect = (item) => {
    this.props.onSelect(item);
    return ''; // erase autocomplete input
  };

  onToggleRequired = item => () => {
    this.props.onToggleRequired(item);
  };

  onChangeLevel = item => () => {
    this.props.onChangeLevel(item);
  };

  onRemove = item => () => {
    this.props.onRemove(item);
  };

  onToggleMode = item => () => {
    this.props.onToggleMode(item);
  };

  onEnterKeyPress = (value, suggestions) => {
    if (suggestions.length > 0) {
      this.onSelect(suggestions[0]);
      return;
    }

    if (value) {
      const fulltextItem = createFulltextItem(this.props.t, value);
      this.onSelect(fulltextItem);
    }
  };

  renderSuggestion = suggestion => (
    <div className={styles.suggestion}>
      <Icon type={suggestion.soft ? 'soft-skill' : suggestion.type} slug={suggestion.slug} />
      {suggestion.title}
    </div>
  );

  canChangeLevel = item => !item.soft && changeLevelAllowed.includes(item.type) && this.props.onChangeLevel !== null;

  getItemStyle = (item, isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    // backgroundColor: item.type === 'rate' && isDragging ? 'red' : undefined,
    // boxShadow: isDragging ? '0px 0px 8px rgba(0, 0, 0, 0.5)' : undefined,
    // padding: grid * 2,
    // margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    // background: isDragging ? 'lightgreen' : 'grey',

    // styles we need to apply on draggables
    ...draggableStyle
  });

  renderSelectedItem = (item, index) => {
    const requireToggleSupported = this.props.id === 'required' && (item.type === 'skill' || item.type === 'language');
    const modeToggleSupported = item.type === 'skill' || item.type === 'language';

    return (
      <Draggable
        key={item.id}
        draggableId={item.id}
        index={index}
      >
        {(providedItem, providedSnapshot) => (
          <span
            ref={providedItem.innerRef}
            {...providedItem.draggableProps}
            {...providedItem.dragHandleProps}
            className={styles.item}
            style={this.getItemStyle(
              item,
              providedSnapshot.isDragging,
              providedItem.draggableProps.style
            )}
          >
            <Tag
              slug={item.slug}
              type={item.type}
              subType={item.soft ? 'soft' : null}
              kind="search"
              isRequired={requireToggleSupported ? item.required : false}
              level={this.canChangeLevel(item) ? item.levelId : undefined}
              mode={item.mode}
              onClick={this.canChangeLevel(item) ? this.onChangeLevel(item) : undefined}
              onRemove={this.onRemove(item)}
              onToggleRequired={requireToggleSupported ? this.onToggleRequired(item) : null}
              onToggleMode={modeToggleSupported ? this.onToggleMode(item) : null}
              actionDisabled={this.props.disabled}
              useChildrenForTooltip={item.type === 'companySize'}
            >
              {item.type === 'companySize' ? this.props.t(`companySizeDetail.${item.slug}`) : item.title}
            </Tag>
          </span>
        )}
      </Draggable>
    );
  };

  render() {
    const {
      id,
      selected,
      loading,
      onChange,
      onRemoveLastSelected,
      onSuggestionsContainerDisplay,
      disabled,
      externalValue,
      t
    } = this.props;

    return (
      <Droppable droppableId={id} direction="horizontal">
        {provided => (
          <div ref={provided.innerRef} id={id} className={styles.anySuggestion}>
            {selected.map(this.renderSelectedItem)}
            {provided.placeholder}
            <div className={styles.atcWrapper}>
              <div className={styles.atcWrapperInner}>
                <Autocomplete
                  id={`${id}-atc`}
                  disabled={disabled}
                  loading={loading}
                  delay={300}
                  createUnknown={this.createUnknown}
                  placeholder={t('suggestion.placeholder')}
                  fetchSuggestions={this.loadSuggestions}
                  renderSuggestionKey="title"
                  onChange={onChange}
                  onSelect={this.onSelect}
                  renderSuggestion={this.renderSuggestion}
                  onRemoveLastSelected={onRemoveLastSelected}
                  onSuggestionsContainerDisplay={onSuggestionsContainerDisplay}
                  onEnterKeyPress={this.onEnterKeyPress}
                  externalValue={externalValue}
                />
              </div>
            </div>
          </div>
        )}
      </Droppable>
    );
  }
}

const AnySuggestionWithTranslation = withTranslation('common')(AnySuggestion);

export default AnySuggestionWithTranslation;
